import { useState, useEffect } from "react";
import Sidebar from "../components/layout/Sidebar";
import Header from "../components/layout/Header";
import Dialog from "../components/dashboard/Dialog";
import { useToast } from "../components/providers/ToastProvider";
import { useSession } from "../context/AuthContext";
import { usePlans } from "../context/PlanContext";
import { useNavigate, Link } from "react-router-dom";
import api from "../services/api";

export default function AdminStudents() {
  const { status } = useSession();
  const { plans: globalPlans, getPlanByName } = usePlans();
  const navigate = useNavigate();
  const { showToast } = useToast();
  
  const [students, setStudents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterMembership, setFilterMembership] = useState("ALL");
  
  // Modals state
  const [isTierModalOpen, setIsTierModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [selectedPlanIds, setSelectedPlanIds] = useState<number[]>([]);
  const [plans, setPlans] = useState<any[]>([]);
  const [addForm, setAddForm] = useState({ name: "", email: "", password: "", membership: "GROWTH" });

  const generatePassword = (name: string) => {
    if (!name.trim()) return;
    const firstName = name.trim().split(" ")[0];
    const formattedName = firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
    const base = `${formattedName}@`;
    
    // Ensure we meet the 8 character minimum limit:
    const numDigits = Math.max(3, 8 - base.length);
    
    let randomNum = "";
    for (let i = 0; i < numDigits; i++) {
      randomNum += Math.floor(Math.random() * 10).toString();
    }
    
    setAddForm(prev => ({...prev, password: `${base}${randomNum}`}));
  };

  const fetchStudents = async () => {
    try {
      const res = await api.get('/admin/students');
      setStudents(res.data);
    } catch (err) {
      console.error("Failed to fetch students:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const res = await api.get('/admin/subscription-plans');
      setPlans(res.data);
    } catch (err) {
      console.error("Failed to fetch plans:", err);
    }
  };

  useEffect(() => {
    if (status === "unauthenticated") {
       navigate("/login");
    } else if (status === "authenticated") {
       fetchStudents();
       fetchPlans();
    }
  }, [status, navigate]);

  const handleTierUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStudent) return;
    setIsSubmitting(true);
    try {
      await api.patch(`/admin/students/${selectedStudent.id}/membership`, { plan_ids: selectedPlanIds });
      setIsTierModalOpen(false);
      showToast("Access Level Updated", `${selectedStudent.name}'s clearance has been re-authorized.`, "success");
      fetchStudents();
    } catch (err) {
      console.error("Tier update failed", err);
      showToast("Update Failed", "Could not synchronize the new membership tier.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddStudent = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await api.post('/admin/students', addForm);
      setIsAddModalOpen(false);
      setAddForm({ name: "", email: "", password: "", membership: "GROWTH" });
      showToast("Student Onboarded", `${addForm.name} is now registered in the academy. Access credentials have been successfully sent to their email address.`, "success");
      fetchStudents();
    } catch (err: any) {
      console.error("Creation failed", err);
      let errorMessage = err.response?.data?.error || "System rejected the onboarding protocol.";
      
      if (err.response?.data?.details) {
          const details = err.response.data.details;
          const firstError = Object.values(details).flat()[0];
          if (firstError) errorMessage = `${firstError}`;
      }
      
      showToast("Onboarding Failed", errorMessage, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!selectedStudent) return;
    setIsSubmitting(true);
    try {
      await api.delete(`/admin/students/${selectedStudent.id}`);
      setIsDeleteModalOpen(false);
      showToast("Record Expunged", `${selectedStudent.name} has been removed from the census.`, "success");
      setSelectedStudent(null);
      fetchStudents();
    } catch (err) {
      console.error("Deletion failed", err);
      showToast("Deletion Blocked", "System could not expunge the record.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBlockToggle = async (e: React.MouseEvent, student: any) => {
    e.stopPropagation();
    setIsSubmitting(true);
    const newStatus = !student.isBlocked;
    try {
      await api.patch(`/admin/students/${student.id}/block`, { isBlocked: newStatus });
      showToast(newStatus ? "Access Revoked" : "Access Restored", `${student.name || "Student"} has been ${newStatus ? 'blocked from' : 'granted access to'} the platform.`, newStatus ? "error" : "success");
      fetchStudents();
    } catch (err) {
      console.error("Block toggle failed", err);
      showToast("Update Failed", "Could not change the block status.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredStudents = students.filter(s => {
    const matchesSearch = s.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          s.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesMembership = filterMembership === "ALL" || s.membership === filterMembership;
    return matchesSearch && matchesMembership;
  });

  const getTierDetails = (tier?: string) => {
    if (!tier) return { color: "bg-slate-500/10 text-slate-500 border-slate-500/20", icon: "person" };
    const plan = getPlanByName(tier);
    if (plan && plan.icon_color) {
      return { color: `bg-[${plan.icon_color}]/10 text-[${plan.icon_color}] border-[${plan.icon_color}]/20`, icon: "military_tech" };
    }
    switch (tier.toUpperCase()) {
      case "VIP": return { color: "bg-cyan-500/10 text-cyan-500 border-cyan-500/20", icon: "auto_awesome" };
      case "PREMIUM": return { color: "bg-amber-500/10 text-amber-500 border-amber-500/20", icon: "workspace_premium" };
      case "GROWTH": return { color: "bg-slate-500/10 text-slate-500 border-slate-500/20", icon: "military_tech" };
      default: return { color: "bg-slate-500/10 text-slate-500 border-slate-500/20", icon: "person" };
    }
  };

  return (
    <div className="min-h-screen bg-background text-on-background">
      <Header />
      <Sidebar />
      
      <main className="ml-0 md:ml-20 pt-20 pb-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto space-y-8 sm:space-y-12">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-6">
            <div className="space-y-2">
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">Student Governance</span>
              <h1 className="text-3xl sm:text-5xl font-black text-on-surface tracking-tighter leading-tight">
                Academic <span className="text-outline-variant/40">Census.</span>
              </h1>
            </div>
            <button 
              onClick={() => setIsAddModalOpen(true)}
              className="w-full sm:w-auto px-6 py-4 bg-primary text-surface rounded-2xl font-black text-xs uppercase tracking-widest hover:translate-y-[-4px] hover:shadow-2xl transition-all shadow-xl shadow-primary/20 flex items-center justify-center gap-2"
            >
              <span className="material-symbols-outlined text-sm">person_add</span>
              Onboard Student
            </button>
          </div>

          {/* Controls: Search & Filter */}
          <div className="flex flex-row gap-3 sm:gap-4 items-center">
            <div className="relative group flex-1">
              <span className="material-symbols-outlined absolute left-5 sm:left-6 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors">search</span>
              <input 
                type="text" 
                placeholder="Locate student by name or email identity..."
                className="w-full pl-12 sm:pl-16 pr-6 py-4 bg-surface-container-low border border-outline-variant/10 rounded-[1.5rem] sm:rounded-[2rem] outline-none focus:ring-4 focus:ring-primary/5 focus:border-primary/20 transition-all text-sm font-medium shadow-sm text-on-surface"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            <div className="relative shrink-0 group" title="Filter by Membership">
              <span className={`material-symbols-outlined absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none transition-colors z-10 ${filterMembership !== "ALL" ? "text-primary" : "text-slate-400 group-hover:text-primary"}`}>
                {filterMembership === "ALL" ? "filter_list" : getTierDetails(filterMembership).icon}
              </span>
              <select
                className="w-[3.25rem] h-[3.25rem] sm:w-[3.75rem] sm:h-[3.75rem] bg-surface-container-low border border-outline-variant/10 rounded-full outline-none focus:ring-4 focus:ring-primary/5 focus:border-primary/20 transition-all appearance-none cursor-pointer text-transparent"
                value={filterMembership}
                onChange={(e) => setFilterMembership(e.target.value)}
              >
                <option value="ALL" className="text-black dark:text-white">All Memberships</option>
                {globalPlans.map(plan => (
                  <option key={plan.name} value={plan.name.toUpperCase()} className="text-black dark:text-white">{plan.name} Tier</option>
                ))}
              </select>
              {filterMembership !== "ALL" && (
                <div className="absolute top-1 right-1 w-3 h-3 bg-primary rounded-full border-2 border-background z-20" />
              )}
            </div>
          </div>

          {/* Census Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {loading ? (
              [1, 2, 3].map(i => (
                <div key={i} className="h-56 bg-surface-container-low rounded-[1.75rem] sm:rounded-[2.5rem] animate-pulse border border-outline-variant/5" />
              ))
            ) : filteredStudents.length > 0 ? filteredStudents.map((student) => {
              const tier = getTierDetails(student.membership);
              return (
                <div key={student.id} className="bg-surface-container-low p-6 sm:p-8 rounded-[1.75rem] sm:rounded-[2.5rem] border border-outline-variant/10 shadow-sm hover:shadow-xl transition-all group relative overflow-hidden flex flex-col justify-between min-h-[240px]">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl group-hover:bg-primary/10 transition-colors" />
                  
                  <div>
                    <div className="flex justify-between items-start mb-6 relative z-20">
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-black text-lg ${tier.color}`}>
                        {student.name?.[0] || "S"}
                      </div>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedStudent(student);
                          setSelectedPlanIds(student.subscriptions?.map((s: any) => Number(s.plan_id)) || []);
                          setIsTierModalOpen(true);
                        }}
                        className={`px-3 py-1 sm:px-4 sm:py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest border flex items-center gap-2 hover:scale-105 transition-transform ${tier.color}`}
                      >
                        <span className="material-symbols-outlined text-[12px]">{tier.icon}</span>
                        {student.highest_plan_name || "FREE"}
                      </button>
                    </div>

                    <Link to={`/admin/students/${student.id}`} className="block space-y-1 mb-6 hover:opacity-70 transition-opacity relative z-10">
                      <h3 className="text-lg sm:text-xl font-black text-on-surface tracking-tight truncate group-hover:text-primary transition-colors">{student.name || "Unnamed Student"}</h3>
                      <p className="text-xs font-medium text-slate-500 dark:text-slate-400 truncate">{student.email}</p>
                    </Link>
                  </div>

                  <div className="flex items-center justify-between pt-6 border-t border-outline-variant/10 gap-2">
                    <div className="flex flex-col min-w-0">
                        <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">Enrollment Date</span>
                        <span className="text-[10px] font-bold text-slate-600 dark:text-slate-300 truncate">
                          {new Date(student.created_at || student.createdAt).toLocaleDateString()}
                        </span>
                    </div>
                    <div className="flex items-center gap-2 relative z-20 flex-shrink-0">
                        <button 
                          onClick={(e) => handleBlockToggle(e, student)}
                          disabled={isSubmitting}
                          title={student.isBlocked ? "Unblock Student" : "Block Student"}
                          className={`w-10 h-10 rounded-xl transition-all flex items-center justify-center ${student.isBlocked ? 'bg-amber-500/10 text-amber-500 hover:bg-amber-500/20' : 'bg-slate-500/5 text-slate-400 hover:text-amber-500 hover:bg-amber-500/10'}`}
                        >
                          <span className="material-symbols-outlined text-sm">{student.isBlocked ? 'lock' : 'lock_open'}</span>
                        </button>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedStudent(student);
                            setIsDeleteModalOpen(true);
                          }}
                          className="w-10 h-10 rounded-xl bg-red-500/5 text-red-500/40 hover:text-red-500 hover:bg-red-500/10 transition-all flex items-center justify-center"
                        >
                          <span className="material-symbols-outlined text-sm">delete</span>
                        </button>
                    </div>
                  </div>
                </div>
              );
            }) : (
              <div className="col-span-full py-20 text-center space-y-4 opacity-30">
                 <span className="material-symbols-outlined text-6xl">person_search</span>
                 <p className="text-xl font-black tracking-tight">Zero students detected in this sector of the census.</p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Tier Management Modal */}
      <Dialog 
        isOpen={isTierModalOpen} 
        onClose={() => setIsTierModalOpen(false)} 
        title="Administer Membership"
      >
        <form onSubmit={handleTierUpdate} className="space-y-6 pt-2">
          <div className="space-y-3 text-center">
            <div className="w-14 h-14 bg-primary/10 text-primary rounded-2xl flex items-center justify-center mx-auto">
              <span className="material-symbols-outlined text-2xl">upgrade</span>
            </div>
            <div>
              <h4 className="font-black text-lg tracking-tight">Assign Subscription Plans</h4>
              <p className="text-xs font-medium text-slate-500">Modify the student's operational privileges immediately.</p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 max-h-60 overflow-y-auto p-2">
            {/* Free Plan Option */}
            <button
              type="button"
              onClick={() => setSelectedPlanIds([])}
              className={`p-4 rounded-2xl border-2 transition-all text-left flex items-center gap-3 group ${
                selectedPlanIds.length === 0
                  ? "border-primary bg-primary/5 shadow-md" 
                  : "border-outline-variant/10 hover:border-primary/30"
              }`}
            >
              <div className="flex-1 min-w-0">
                <span className={`block text-[10px] font-black tracking-widest uppercase truncate ${selectedPlanIds.length === 0 ? "text-primary" : "text-slate-500"}`}>
                  FREE
                </span>
                <span className="block text-xs font-bold text-slate-400 mt-0.5 truncate">
                  Tier 0
                </span>
              </div>
              <span className={`material-symbols-outlined transition-colors ${selectedPlanIds.length === 0 ? "text-primary" : "text-transparent"}`}>
                check_circle
              </span>
            </button>

            {plans.map((plan: any) => (
              <button
                key={plan.id}
                type="button"
                onClick={() => {
                  setSelectedPlanIds(prev => 
                    prev.includes(Number(plan.id)) ? prev.filter(id => id !== Number(plan.id)) : [...prev, Number(plan.id)]
                  );
                }}
                className={`p-4 rounded-2xl border-2 transition-all text-left flex items-center gap-3 group ${
                  selectedPlanIds.includes(Number(plan.id))
                    ? "border-primary bg-primary/5 shadow-md" 
                    : "border-outline-variant/10 hover:border-primary/30"
                }`}
              >
                <div className="flex-1 min-w-0">
                  <span className={`block text-[10px] font-black tracking-widest uppercase truncate ${selectedPlanIds.includes(Number(plan.id)) ? "text-primary" : "text-slate-500"}`}>
                    {plan.name}
                  </span>
                  <span className="text-[9px] font-bold text-slate-400">Tier {plan.tier_level}</span>
                </div>
                {selectedPlanIds.includes(Number(plan.id)) && <span className="material-symbols-outlined text-primary text-sm">check_circle</span>}
              </button>
            ))}
          </div>

          <button 
            disabled={isSubmitting}
            className="w-full py-4 sm:py-5 bg-primary text-surface rounded-[1.25rem] sm:rounded-[2rem] font-black text-xs uppercase tracking-widest hover:translate-y-[-2px] hover:shadow-2xl transition-all shadow-xl shadow-primary/20 mt-2"
          >
            {isSubmitting ? "Updating Clearance..." : "Finalize Transformation"}
          </button>
        </form>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog 
        isOpen={isDeleteModalOpen} 
        onClose={() => setIsDeleteModalOpen(false)} 
        title="Expunge Record?"
        className="!max-w-md border-red-500/20"
      >
        <div className="space-y-6 text-center py-2">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-red-500/10 text-red-500 rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto mb-4">
            <span className="material-symbols-outlined text-3xl sm:text-4xl">no_accounts</span>
          </div>
          <div className="space-y-2">
            <h4 className="text-lg sm:text-xl font-black text-on-surface tracking-tight">Permanent Expulsion</h4>
            <p className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400 leading-relaxed px-2">
              You are about to expunge <span className="text-on-surface font-black italic">{selectedStudent?.name}</span> from the census. This data purge is irreversible.
            </p>
          </div>
          <div className="flex gap-3 sm:gap-4 pt-4">
            <button 
              onClick={() => setIsDeleteModalOpen(false)}
              className="flex-1 py-3.5 bg-surface-container-high text-on-surface-variant rounded-xl sm:rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-surface-container-highest transition-all"
            >
              Abeyance
            </button>
            <button 
              onClick={confirmDelete}
              disabled={isSubmitting}
              className="flex-1 py-3.5 bg-red-500 text-white rounded-xl sm:rounded-2xl font-black text-xs uppercase tracking-widest hover:translate-y-[-2px] hover:shadow-xl hover:shadow-red-500/20 transition-all active:scale-95 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : "Expunge Record"}
            </button>
          </div>
        </div>
      </Dialog>

      {/* Add Student Modal */}
      <Dialog 
        isOpen={isAddModalOpen} 
        onClose={() => setIsAddModalOpen(false)} 
        title="Onboard New Student"
      >
        <form onSubmit={handleAddStudent} className="space-y-6 pt-2">
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 px-1">Full Identity</label>
              <input 
                type="text" 
                required
                placeholder="e.g. Alexander Pierce"
                className="w-full px-5 py-3.5 bg-surface-container-high text-on-surface border border-outline-variant/10 rounded-xl outline-none focus:border-primary/30 transition-all text-sm font-bold"
                value={addForm.name}
                onChange={(e) => setAddForm({...addForm, name: e.target.value})}
                onBlur={(e) => generatePassword(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 px-1">Communication Channel (Email)</label>
              <input 
                type="email" 
                required
                placeholder="alex@hitclicks.com"
                className="w-full px-5 py-3.5 bg-surface-container-high text-on-surface border border-outline-variant/10 rounded-xl outline-none focus:border-primary/30 transition-all text-sm font-bold"
                value={addForm.email}
                onChange={(e) => setAddForm({...addForm, email: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 px-1">Access Credentials (Password)</label>
              <div className="relative">
                <input 
                  type={showPassword ? "text" : "password"} 
                  required
                  placeholder="••••••••"
                  className="w-full px-5 py-3.5 bg-surface-container-high text-on-surface border border-outline-variant/10 rounded-xl outline-none focus:border-primary/30 transition-all text-sm font-bold pr-20"
                  value={addForm.password}
                  onChange={(e) => setAddForm({...addForm, password: e.target.value})}
                />
                <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => generatePassword(addForm.name)}
                    className="text-slate-400 hover:text-primary transition-colors flex items-center justify-center"
                    title="Auto-Generate Password"
                  >
                    <span className="material-symbols-outlined text-[20px]">
                      auto_awesome
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-slate-400 hover:text-primary transition-colors flex items-center justify-center"
                  >
                    <span className="material-symbols-outlined text-[20px]">
                      {showPassword ? "visibility_off" : "visibility"}
                    </span>
                  </button>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 px-1">Initial Membership Assignment</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {["FREE", ...globalPlans.map(p => p.name.toUpperCase())].map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setAddForm({...addForm, membership: t})}
                    className={`py-3 rounded-lg border font-black text-[10px] tracking-widest transition-all ${
                      addForm.membership === t 
                        ? "bg-primary text-white border-primary shadow-lg shadow-primary/20" 
                        : "bg-surface-container-high text-slate-500 border-outline-variant/10 hover:border-primary/20"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="pt-2">
              <button 
                type="submit" 
                disabled={isSubmitting}
                className={`w-full py-4 sm:py-5 bg-primary text-surface rounded-[1.25rem] sm:rounded-[2rem] font-black text-xs uppercase tracking-widest transition-all shadow-xl shadow-primary/20 flex items-center justify-center gap-3 ${isSubmitting ? 'opacity-80 cursor-wait transform-none' : 'hover:translate-y-[-2px] hover:shadow-2xl'}`}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-4 w-4 sm:h-5 sm:w-5 text-surface" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Syncing with Hub...
                  </>
                ) : (
                  "Complete Onboarding"
                )}
              </button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
